/**
 * La conversación de la Fase D: el banco PIDE y la mesa ACEPTA.
 *
 * Dos piezas que son las dos mitades de lo mismo:
 *
 *  - `RequestPanel` — la tablet del banco. Cuatro pedidos y nada más, porque
 *    son los cuatro que el reglamento le reconoce al banco (formación 7.3.2,
 *    sustitución 15.5, tiempo 15.4, challenge VCS art. 4). No hay un botón para
 *    escribir el acta, y no es un olvido: **el banco no escribe el log** (§5.2),
 *    que es lo que evita que cinco dispositivos numeren el mismo partido.
 *  - `QueuePanel` — la bandeja del anotador. CEV lo dice mejor que nosotros:
 *    *"The scorer's function, in this case, changes from inputting data to
 *    checking data, and then accepting (or rejecting) that data"*.
 *
 * ## LO QUE PASA CUANDO NO HAY RED (§5.3)
 *
 * Un pedido que no sale **no se encola en silencio**. Un cambio pedido hace
 * tres minutos que llega ahora es un cambio equivocado: el rally siguiente ya
 * se jugó. Se reintenta unos segundos —un parpadeo de red no es una caída— y
 * después la pantalla dice lo que el reglamento ya dice: **pedilo con la seña**.
 * Ése es el pedido oficial de verdad (el jugador entrando a la zona; para el
 * challenge, VCS art. 21 contempla explícitamente que la ETT falle y se pida
 * con seña y voz). El server además vence los pedidos viejos por su cuenta, así
 * que ni siquiera un cliente roto puede meter un cambio tarde.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { TeamSide } from '@scout/core';
import { useT } from '../../i18n/use-t';
import type { MessageKey } from '../../i18n/index';
import { ApiError, type ApiClient, type CreateIntentBody } from '../../lib/api-client';
import {
  RETRY_WINDOW_MS,
  sendOutcome,
  sortQueue,
  substitutionOptions,
  type BenchIntent,
  type BenchSnapshot,
  type IntentKind,
} from '../../lib/bench';
import { Age } from './BenchPanels';

const RETRY_DELAY_MS = 1_500;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

// ---------------------------------------------------------------------------
// El envío, con su caída
// ---------------------------------------------------------------------------

interface SendState {
  /** Qué se está mandando (o `null`). */
  pending: IntentKind | null;
  /** Se agotó el reintento: hay que hacerlo con la seña. */
  fallback: IntentKind | null;
  /** Error definitivo del server (403, 409, 422): no se reintenta. */
  error: string | null;
}

const IDLE: SendState = { pending: null, fallback: null, error: null };

/**
 * Manda un pedido y decide qué hacer si no sale.
 *
 * Sólo se reintenta el fallo de RED. Un 403 (no estás designado), un 409 (el
 * acta ya se cerró) o un 422 no se arreglan insistiendo, y reintentarlos sería
 * esconderle al DT la razón real.
 */
function useSender(
  client: ApiClient,
  matchId: string,
  onIntent: (intent: BenchIntent) => void,
): [SendState, (body: CreateIntentBody, kind: IntentKind) => void] {
  const [state, setState] = useState<SendState>(IDLE);
  // El reintento vive fuera de React (un `for` con `await`), así que necesita
  // saber por su cuenta que la pantalla se fue: sin esto, un banco que cambia
  // de vista mientras se reintenta seguiría escribiendo estado de un componente
  // desmontado.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const submit = useCallback(
    (body: CreateIntentBody, kind: IntentKind) => {
      const startedAt = Date.now();
      setState({ pending: kind, fallback: null, error: null });
      void (async () => {
        for (;;) {
          try {
            const { intent } = await client.createIntent(matchId, body);
            if (!alive.current) return;
            onIntent(intent);
            setState(IDLE);
            return;
          } catch (err) {
            if (!alive.current) return;
            const esRed = err instanceof ApiError && err.kind === 'network';
            if (!esRed) {
              setState({
                pending: null,
                fallback: null,
                error: err instanceof Error ? err.message : String(err),
              });
              return;
            }
            if (sendOutcome(startedAt, Date.now(), RETRY_WINDOW_MS) === 'FALL_BACK_TO_SIGNAL') {
              setState({ pending: null, fallback: kind, error: null });
              return;
            }
            await sleep(RETRY_DELAY_MS);
          }
        }
      })();
    },
    [client, matchId, onIntent],
  );

  return [state, submit];
}

// ---------------------------------------------------------------------------
// La tablet del banco
// ---------------------------------------------------------------------------

export function RequestPanel({
  client,
  snapshot,
  team,
  onIntent,
  onLineup,
}: {
  client: ApiClient;
  snapshot: BenchSnapshot;
  team: TeamSide;
  onIntent: (intent: BenchIntent) => void;
  /** Con esto aparece el pedido "Formación" (lleva al panel donde se arma). */
  onLineup?: (() => void) | undefined;
}): JSX.Element {
  const t = useT();
  const [state, submit] = useSender(client, snapshot.matchId, onIntent);
  const [saliendo, setSaliendo] = useState<number | null>(null);

  const equipo = snapshot.teams[team];
  const subs = equipo.substitutions;
  const opciones = substitutionOptions(equipo, snapshot.meta[team]);
  const elegido = opciones.find((o) => o.playerOut === saliendo) ?? null;

  const sinTiempos = equipo.timeoutsUsed >= equipo.timeoutsLimit;
  const sinChallenge = equipo.challengesRemaining <= 0;
  const revisionEnCurso = snapshot.challenge.open;

  return (
    <section className="bench-card requests">
      <header className="bench-card-head">
        <h2>{t('bench.request.title')}</h2>
      </header>

      {state.fallback !== null ? (
        <div className="bench-banner bad" role="alert">
          <div className="bench-banner-head">{t('bench.request.noNetwork')}</div>
          <div className="bench-banner-body">{t('bench.request.useSignal')}</div>
        </div>
      ) : null}
      {state.error !== null ? (
        <div className="bench-banner warn" role="alert">
          <div className="bench-banner-body">{state.error}</div>
        </div>
      ) : null}

      <div className="bench-actions">
        <button
          type="button"
          className="bench-btn big kind-timeout"
          disabled={sinTiempos || revisionEnCurso || state.pending !== null}
          onClick={() => submit({ intent: { kind: 'timeout' } }, 'timeout')}
        >
          {state.pending === 'timeout' ? t('bench.request.sending') : t('bench.request.timeout')}
          <small>{t('bench.score.timeoutsLeft2', { n: equipo.timeoutsLimit - equipo.timeoutsUsed })}</small>
        </button>

        <button
          type="button"
          className="bench-btn big kind-challenge"
          disabled={sinChallenge || revisionEnCurso || state.pending !== null}
          onClick={() => submit({ intent: { kind: 'challenge', claimedFault: null } }, 'challenge')}
        >
          {state.pending === 'challenge' ? t('bench.request.sending') : t('bench.request.challenge')}
          <small>{t('bench.score.challengesLeft2', { n: equipo.challengesRemaining })}</small>
        </button>

        {onLineup ? (
          <button type="button" className="bench-btn big kind-lineup" onClick={onLineup}>
            {t('bench.queue.kinds.lineup')}
            <small>{t('benchPanel.lineupHint')}</small>
          </button>
        ) : null}
      </div>
      {revisionEnCurso ? <p className="bench-hint">{t('bench.request.underReview')}</p> : null}

      {/* --- Sustitución -----------------------------------------------------
          El marco amarillo aparece al pedir la QUINTA y el botón se apaga
          cuando las seis ya se usaron (regla 15.1). Apagado NO es callado: la
          pantalla dice cuál es la salida, que es la sustitución EXCEPCIONAL del
          15.7 —con la seña, y la autoriza el 2do árbitro—. Una validación sin
          salida es una jaula, y en cancha gana la realidad (§10.4.2). */}
      <div className={`bench-sub${subs.warn ? ' warn' : ''}${subs.blocked ? ' blocked' : ''}`}>
        <div className="bench-sub-head">
          <span>{t('bench.request.substitution')}</span>
          <span className="bench-sub-count">
            {t('bench.request.subsUsed', { used: subs.used, limit: subs.limit })}
          </span>
        </div>

        {subs.blocked ? (
          <p className="bench-hint">{t('bench.request.noSubsLeft')}</p>
        ) : (
          <>
            {subs.warn ? <p className="bench-hint warn">{t('bench.request.subsWarn')}</p> : null}
            <div className="bench-picker">
              <span className="bench-picker-label">{t('bench.request.whoLeaves')}</span>
              <div className="bench-numbers">
                {opciones.map((option) => (
                  <button
                    key={option.playerOut}
                    type="button"
                    className={`bench-num${saliendo === option.playerOut ? ' picked' : ''}`}
                    disabled={option.blocked}
                    title={option.blocked ? t('bench.request.cannotLeave') : undefined}
                    onClick={() => setSaliendo(option.playerOut === saliendo ? null : option.playerOut)}
                  >
                    {option.playerOut}
                  </button>
                ))}
              </div>
            </div>

            {elegido !== null ? (
              <div className="bench-picker">
                <span className="bench-picker-label">{t('bench.request.whoEnters')}</span>
                {elegido.lockedTo !== null ? (
                  <p className="bench-hint">
                    {t('bench.request.pairLocked', { player: elegido.lockedTo })}
                  </p>
                ) : null}
                <div className="bench-numbers">
                  {elegido.candidates.length === 0 ? (
                    <span className="bench-hint">{t('bench.request.noCandidates')}</span>
                  ) : (
                    elegido.candidates.map((playerIn) => (
                      <button
                        key={playerIn}
                        type="button"
                        className={`bench-num${elegido.lockedTo === playerIn ? ' paired' : ''}`}
                        disabled={state.pending !== null}
                        onClick={() => {
                          submit(
                            {
                              intent: {
                                kind: 'substitution',
                                playerOut: elegido.playerOut,
                                playerIn,
                              },
                            },
                            'substitution',
                          );
                          setSaliendo(null);
                        }}
                      >
                        {playerIn}
                      </button>
                    ))
                  )}
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

/**
 * La formación inicial, mandada desde la tablet del banco.
 *
 * Es el pedido con más valor reglamentario de los cuatro: la regla 5.2.3.1 dice
 * que *"si se usan aplicaciones de tablet, la transmisión electrónica de la
 * formación se considera automáticamente oficial"*, y la 7.3.2 admite que la
 * ficha se mande *"electronically sent directly to the e-scorer"*. O sea que
 * esta pantalla no es una comodidad: es la ficha R-5, y convierte la tablet del
 * banco en fuente legal de la formación.
 *
 * Se carga tocando los números EN ORDEN P1..P6 —que es el orden del acta y el
 * orden en el que el DT los dice en voz alta— y no con seis desplegables: un
 * `<select>` por posición son seis menús que se abren tapando la pantalla.
 */
export function LineupPanel({
  client,
  snapshot,
  team,
  onIntent,
}: {
  client: ApiClient;
  snapshot: BenchSnapshot;
  team: TeamSide;
  onIntent: (intent: BenchIntent) => void;
}): JSX.Element {
  const t = useT();
  const [state, submit] = useSender(client, snapshot.matchId, onIntent);
  const [rotation, setRotation] = useState<number[]>([]);

  const meta = snapshot.meta[team];
  const liberos = new Set([...meta.liberos, ...meta.players.filter((p) => p.libero).map((p) => p.number)]);
  // El líbero NO entra en la formación inicial (regla 19.1.2: va en las líneas
  // especiales del acta, no en las seis posiciones).
  const disponibles = meta.players.map((p) => p.number).filter((n) => !liberos.has(n));
  const elegido = new Set(rotation);
  // El set que se manda es el PRÓXIMO cuando el anterior ya terminó.
  const setNumber = Math.max(1, snapshot.currentSet === 0 ? 1 : snapshot.currentSet);

  return (
    <section className="bench-card" id="bench-lineup">
      <header className="bench-card-head">
        <h2>{t('bench.lineup.title', { set: setNumber })}</h2>
      </header>
      <p className="bench-hint">{t('bench.lineup.official')}</p>

      <div className="bench-lineup-slots">
        {[1, 2, 3, 4, 5, 6].map((position) => (
          <div key={position} className={`bench-cell${rotation.length === position - 1 ? ' picked' : ''}`}>
            <span className="bench-cell-pos">P{position}</span>
            <span className="bench-cell-number">{rotation[position - 1] ?? '—'}</span>
          </div>
        ))}
      </div>

      <div className="bench-numbers">
        {disponibles.map((number) => (
          <button
            key={number}
            type="button"
            className={`bench-num${elegido.has(number) ? ' picked' : ''}`}
            disabled={state.pending !== null || (!elegido.has(number) && rotation.length >= 6)}
            onClick={() =>
              setRotation((prev) =>
                prev.includes(number) ? prev.filter((n) => n !== number) : [...prev, number],
              )
            }
          >
            {number}
          </button>
        ))}
      </div>

      {state.fallback !== null ? (
        <div className="bench-banner bad" role="alert">
          <div className="bench-banner-head">{t('bench.request.noNetwork')}</div>
          <div className="bench-banner-body">{t('bench.lineup.useSheet')}</div>
        </div>
      ) : null}
      {state.error !== null ? <p className="bench-hint bad">{state.error}</p> : null}

      <div className="bench-actions">
        <button
          type="button"
          className="bench-btn big kind-lineup"
          disabled={rotation.length !== 6 || state.pending !== null}
          onClick={() => submit({ intent: { kind: 'lineup', setNumber, rotation } }, 'lineup')}
        >
          {state.pending === 'lineup' ? t('bench.request.sending') : t('bench.lineup.send')}
        </button>
        <button
          type="button"
          className="bench-btn ghost"
          disabled={rotation.length === 0}
          onClick={() => setRotation([])}
        >
          {t('bench.lineup.clear')}
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// La cola
// ---------------------------------------------------------------------------

function kindLabel(kind: IntentKind, t: ReturnType<typeof useT>): string {
  switch (kind) {
    case 'timeout':
      return t('bench.queue.kinds.timeout');
    case 'substitution':
      return t('bench.queue.kinds.substitution');
    case 'challenge':
      return t('bench.queue.kinds.challenge');
    case 'lineup':
      return t('bench.queue.kinds.lineup');
  }
}

/** Resumen legible del pedido: qué se está pidiendo exactamente. */
function detailOf(intent: BenchIntent, t: ReturnType<typeof useT>): string {
  if (intent.kind === 'substitution') {
    const out = intent.payload['playerOut'];
    const inn = intent.payload['playerIn'];
    return t('bench.queue.subDetail', { out: String(out), in: String(inn) });
  }
  if (intent.kind === 'lineup') {
    const rotation = intent.payload['rotation'];
    return Array.isArray(rotation) ? rotation.join(' · ') : '';
  }
  return '';
}

/**
 * La bandeja. `onResolve` sólo llega en la vista de mesa: el árbitro, el
 * supervisor y la pantalla ven exactamente la misma lista **sin un botón**,
 * que es la definición de read-only que pide §5.4 y lo que hace que las N
 * instancias de árbitro sean gratis.
 */
export function QueuePanel({
  snapshot,
  intents,
  now,
  onResolve = null,
  onCancel = null,
  busyId = null,
}: {
  snapshot: BenchSnapshot;
  intents: readonly BenchIntent[];
  now: number;
  onResolve?: ((intent: BenchIntent, decision: 'ACCEPT' | 'REJECT') => void) | null;
  onCancel?: ((intent: BenchIntent) => void) | null;
  busyId?: string | null;
}): JSX.Element {
  const t = useT();
  const lista = sortQueue(intents);

  return (
    <section className="bench-card queue">
      <header className="bench-card-head">
        <h2>{t('bench.queue.title')}</h2>
      </header>
      {lista.length === 0 ? (
        <p className="bench-empty">{t('bench.queue.empty')}</p>
      ) : (
        <ul className="bench-queue">
          {lista.map((intent) => {
            const vencido = intent.state === 'PENDING' && intent.stale;
            return (
              <li
                key={intent.id}
                className={`bench-request kind-${intent.kind} ${intent.team} ${intent.state.toLowerCase()}${
                  vencido ? ' stale' : ''
                }`}
              >
                <div className="bench-request-head">
                  <span className="bench-request-kind">{kindLabel(intent.kind, t)}</span>
                  <span className="bench-request-team">{snapshot.meta[intent.team].name}</span>
                  <span className={`bench-request-state ${intent.state.toLowerCase()}`}>
                    {t(`benchPanel.state.${intent.state}` as MessageKey)}
                  </span>
                  <Age iso={intent.requestedAt} now={now} />
                </div>
                <div className="bench-request-body">
                  {detailOf(intent, t)}
                  <span className="bench-request-by">
                    {t('bench.queue.by', { who: intent.requestedBy.holderName })}
                  </span>
                </div>

                {vencido ? (
                  // Venció: el juego siguió y esto dejó de ser un pedido. Se
                  // muestra igual (es auditoría) y se dice qué hacer.
                  <p className="bench-hint bad">{t('bench.queue.stale')}</p>
                ) : null}
                {intent.state === 'REJECTED' && intent.reason !== undefined ? (
                  <p className="bench-hint">{t('bench.queue.rejected', { reason: intent.reason })}</p>
                ) : null}
                {intent.state === 'ACCEPTED' ? (
                  <p className="bench-hint ok">
                    {t('bench.queue.accepted', { who: intent.resolvedBy ?? '' })}
                  </p>
                ) : null}
                {intent.state === 'CANCELLED' ? (
                  <p className="bench-hint">{t('bench.queue.cancelled')}</p>
                ) : null}

                {onResolve && intent.state === 'PENDING' && !vencido ? (
                  <div className="bench-request-actions">
                    <button
                      type="button"
                      className="bench-btn ok"
                      disabled={busyId === intent.id}
                      onClick={() => onResolve(intent, 'ACCEPT')}
                    >
                      {t('bench.queue.accept')}
                    </button>
                    <button
                      type="button"
                      className="bench-btn bad"
                      disabled={busyId === intent.id}
                      onClick={() => onResolve(intent, 'REJECT')}
                    >
                      {t('bench.queue.reject')}
                    </button>
                  </div>
                ) : null}
                {onCancel && intent.state === 'PENDING' && !vencido ? (
                  <div className="bench-request-actions">
                    <button
                      type="button"
                      className="bench-btn ghost"
                      disabled={busyId === intent.id}
                      onClick={() => onCancel(intent)}
                    >
                      {t('bench.queue.cancel')}
                    </button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
